import { useEffect, useMemo, useState } from "react";
import { apiClient, useAuth, usePermissions } from "../App";
import { usePmTestMode } from "../hooks/usePmTestMode";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AddChangeOrderModal } from "../components/change-orders/AddChangeOrderModal";
import { RecordManualPaymentDialog } from "../components/change-orders/RecordManualPaymentDialog";
import { DeleteAdditionalScopeDialog } from "../components/change-orders/DeleteAdditionalScopeDialog";
import { canDeleteAdditionalScope, canEditAdditionalScope, isFinanceRole } from "../components/change-orders/additionalScopeAccess";
import { DECISION_LABEL, DECISION_TONE, decisionSummary, itemDecision } from "../components/change-orders/itemDecisions";
import { FileText, Mail, CheckCircle2, AlertTriangle, Clock, DollarSign, ArrowUpRight, Filter, Plus, X, Activity, ChevronDown, ChevronUp, Settings, Trash2, Pencil } from "lucide-react";

const STATUS_LABEL = {
  draft: "Draft",
  ready_to_send: "Ready to Send",
  sent_to_client: "Sent to Client",
  client_approved: "Client Approved",
  invoice_sent: "Invoice Sent",
  paid: "Deposit Paid",
  cleared_for_production: "Cleared for Production",
  in_production: "In Production",
  completed: "Completed",
  fully_paid: "Fully Paid",
  // Legacy (backward compat for existing rows)
  pending_sales_review: "Pending Sales Review",
  needs_pm_clarification: "Needs PM Clarification",
  pending_approval: "Pending Approval",
  approved: "Approved",
  invoiced: "Invoiced",
  payment_pending: "Payment Pending",
  disputed: "Disputed",
  cancelled: "Cancelled",
};
const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  ready_to_send: "bg-blue-50 text-blue-700 border-blue-200",
  sent_to_client: "bg-indigo-50 text-indigo-700 border-indigo-200",
  client_approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  invoice_sent: "bg-sky-50 text-sky-700 border-sky-200",
  paid: "bg-green-100 text-green-800 border-green-300",
  cleared_for_production: "bg-emerald-100 text-emerald-800 border-emerald-300",
  in_production: "bg-purple-50 text-purple-700 border-purple-200",
  completed: "bg-zinc-100 text-zinc-700 border-zinc-200",
  fully_paid: "bg-green-200 text-green-900 border-green-400",
  // Legacy
  pending_sales_review: "bg-amber-50 text-amber-700 border-amber-200",
  needs_pm_clarification: "bg-rose-50 text-rose-700 border-rose-200",
  pending_approval: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  invoiced: "bg-sky-50 text-sky-700 border-sky-200",
  payment_pending: "bg-indigo-50 text-indigo-700 border-indigo-200",
  disputed: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

const VIEW_TABS = [
  { key: "all",            label: "All",              statuses: null },
  { key: "awaiting_client",label: "Awaiting Client",  statuses: ["draft", "ready_to_send", "sent_to_client", "invoice_sent"] },
  { key: "paid",           label: "Paid / Cleared",   statuses: ["paid", "cleared_for_production", "client_approved"] },
  { key: "in_production",  label: "In Production",    statuses: ["in_production"] },
  { key: "completed",      label: "Completed",        statuses: ["completed", "fully_paid"] },
];

function fmt$(n) { return "$" + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"; }
// Client approval/payment links go inactive 7 days after (re)send.
function linkDaysRemaining(co) {
  if (!co.link_expires_at || co.status !== "sent_to_client") return null;
  const ms = new Date(co.link_expires_at).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

export default function ChangeOrdersDashboard() {
  // Financial visibility: PMs (and admins previewing PM mode) never see dollars.
  const { canViewFinancials } = usePermissions();
  const { user } = useAuth();
  const pmTestMode = usePmTestMode();
  const showMoney = canViewFinancials && !pmTestMode.isActive;
  // Deletion is a finance-role question, NOT a money-visibility one: Sales can
  // see dollars here but is not finance, so showMoney is the wrong test.
  const isFinance = isFinanceRole(user?.role) && !pmTestMode.isActive;
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [viewTab, setViewTab] = useState("all");
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState(null);
  // Phase 2: client email composer
  const [emailingCo, setEmailingCo] = useState(null);
  // Phase 3: activity timeline drawer
  const [activityCo, setActivityCo] = useState(null);
  // Manual payment recording (payments made outside the Stripe link)
  const [manualPayCo, setManualPayCo] = useState(null);
  // Change order pending deletion (confirmation dialog target)
  const [deleteCo, setDeleteCo] = useState(null);
  // Change order open in the wizard for editing (null = creating a new one)
  const [editCo, setEditCo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ordersRes, analyticsRes] = await Promise.all([
        apiClient.get("/change-orders"),
        apiClient.get("/change-orders/analytics/summary").catch(() => ({ data: null })),
      ]);
      setOrders((ordersRes.data.change_orders || []).filter(o => o.request_type !== "upsell"));
      setAnalytics(analyticsRes.data);
    } catch (err) {
      toast.error("Failed to load change orders");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    let rows = orders;
    // View tab — bucket-based filter (overrides single-status when set to a non-"all" tab).
    const tab = VIEW_TABS.find(t => t.key === viewTab);
    if (tab?.statuses) rows = rows.filter(o => tab.statuses.includes(o.status));
    if (filter !== "all") rows = rows.filter(o => o.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(o => (o.short_id || "").toLowerCase().includes(q) || (o.project_name || "").toLowerCase().includes(q) || (o.description || "").toLowerCase().includes(q) || (o.title || "").toLowerCase().includes(q));
    }
    return rows;
  }, [orders, filter, viewTab, search]);

  const handleApprove = async (id) => {
    const method = prompt("Approval method? Type 'email' or 'call':", "email");
    if (!method) return;
    const full = method.toLowerCase().startsWith("c") ? "approved_via_call" : "approved_via_email";
    try {
      await apiClient.post(`/change-orders/${id}/approve`, { client_approved: true, approval_method: full });
      toast.success("Approved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const handleStatus = async (id, target) => {
    try {
      await apiClient.patch(`/change-orders/${id}/status`, { status: target });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const handleGenerateInvoice = async (id) => {
    try {
      const res = await apiClient.post(`/change-orders/${id}/generate-invoice`, { send_email: false });
      toast.success(`Invoice ${res.data.invoice.invoice_id} generated`);
      setInvoiceDraft(res.data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // Phase 2 — Stripe Checkout + Email + Production handlers
  const handleGenerateStripe = async (co) => {
    try {
      const res = await apiClient.post(`/change-orders/${co.id}/stripe-checkout`, {
        origin_url: window.location.origin,
      });
      toast.success(`Stripe payment link ready ($${res.data.amount.toLocaleString()})`);
      window.open(res.data.url, "_blank", "noopener,noreferrer");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create Stripe checkout");
    }
  };

  // Phase 3 — Generate & Send mega-button: one-click checkout creation + opens
  // pre-filled email composer with the just-generated payment link embedded.
  const handleGenerateAndSend = async (co) => {
    try {
      const res = await apiClient.post(`/change-orders/${co.id}/stripe-checkout`, {
        origin_url: window.location.origin,
      });
      toast.success(`Payment link generated — $${res.data.amount.toLocaleString()}`);
      // Refresh the list, then open composer with the updated CO that has the new link.
      const list = await apiClient.get("/change-orders");
      setOrders(list.data.change_orders || []);
      const updated = (list.data.change_orders || []).find(c => c.id === co.id) || { ...co, stripe_payment_link: res.data.url };
      setEmailingCo(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate & send");
    }
  };

  const handleStartProduction = async (id) => {
    try {
      await apiClient.post(`/change-orders/${id}/start-production`);
      toast.success("Production started");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const handleSendToClient = async (co) => {
    if (!co.client_email) {
      toast.error("No client email on this record — edit the change order to add one first");
      return;
    }
    try {
      const res = await apiClient.post(`/change-orders/${co.id}/send-to-client`, { message: null });
      toast.success(`Approval link sent to ${co.client_email}`);
      if (res.data.approval_url) {
        navigator.clipboard.writeText(res.data.approval_url).catch(() => {});
        toast.info("Approval link copied to clipboard");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send client email");
    }
  };

  const handleSendBalanceInvoice = async (co) => {
    if (!confirm(`Send the final balance invoice of $${(co.balance_amount || 0).toLocaleString()} to ${co.client_email}?`)) return;
    try {
      await apiClient.post(`/change-orders/${co.id}/send-balance-invoice`);
      toast.success("Balance invoice sent to client");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send balance invoice");
    }
  };

  const handleComplete = async (id) => {
    if (!confirm("Mark this change order as complete?")) return;
    try {
      await apiClient.post(`/change-orders/${id}/complete`);
      toast.success("Change order completed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // Auto-poll Stripe status when redirected back from Checkout (?checkout_session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("checkout_session_id");
    if (!sid) return;
    let attempts = 0;
    let interval;
    const tick = async () => {
      attempts++;
      try {
        const res = await apiClient.get(`/change-orders/checkout-status/${sid}`);
        if (res.data.payment_status === "paid") {
          toast.success("Payment confirmed — change order cleared for production");
          if (interval) clearInterval(interval);
          // Strip query and reload
          window.history.replaceState({}, "", window.location.pathname);
          load();
        } else if (attempts >= 12 || res.data.status === "expired") {
          if (interval) clearInterval(interval);
        }
      } catch { /* keep polling */ }
    };
    tick();
    interval = setInterval(tick, 3000);
    return () => interval && clearInterval(interval);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-6 sm:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-8">
          <div>
            <h1 className="text-[22px] sm:text-[28px] font-light tracking-tight text-slate-900">Change Orders</h1>
            <p className="text-[13px] text-slate-500 mt-1">Track change orders, client approvals, payments, and production clearance.</p>
          </div>
          <Button onClick={() => setAddOpen(true)} className="gap-2 h-9 w-full sm:w-auto" data-testid="add-co-btn">
            <Plus className="w-3.5 h-3.5" /> New Change Order
          </Button>
        </div>

        {/* Metrics — financial, hidden from PMs */}
        {showMoney && analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            <MetricCard label="Total Revenue" value={fmt$(analytics.totals.total_revenue)} icon={DollarSign} />
            <MetricCard label="Paid" value={fmt$(analytics.totals.total_paid)} icon={CheckCircle2} />
            <MetricCard label="Outstanding" value={fmt$(analytics.totals.total_outstanding)} icon={Clock} />
            <MetricCard label="This Month" value={fmt$(analytics.totals.total_this_month)} icon={ArrowUpRight} />
          </div>
        )}

        {/* View Tabs — quick-filter buckets per spec */}
        <div className="flex items-stretch gap-1 rounded-md border border-slate-200 bg-white p-1 mb-3 overflow-x-auto" data-testid="co-view-tabs">
          {VIEW_TABS.map(t => {
            const count = t.statuses ? orders.filter(o => t.statuses.includes(o.status)).length : orders.length;
            const active = viewTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setViewTab(t.key); setFilter("all"); }}
                className={`px-3 py-1.5 rounded text-[12px] font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid={`co-view-tab-${t.key}`}
              >
                {t.label}
                <span className={`text-[10px] tabular-nums px-1.5 rounded ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 h-9 text-[12px] text-slate-500 w-full sm:w-72">
            <Filter className="w-3.5 h-3.5 flex-shrink-0" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, project, description..." className="border-0 shadow-none p-0 h-auto focus-visible:ring-0 text-[13px]" />
          </div>
          <div className="flex items-center gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full sm:w-48 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-[12px] text-slate-400 sm:ml-auto whitespace-nowrap">{visible.length} of {orders.length} shown</span>
          </div>
        </div>

        {/* Table */}
        <Card className="overflow-hidden border-slate-200">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-slate-50/50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Project</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">PM</th>
                <th className="px-4 py-3 font-medium">Status</th>
                {showMoney && <th className="px-4 py-3 font-medium text-right">Amount</th>}
                <th className="px-4 py-3 font-medium">Approved</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-4 py-16 text-center text-slate-400 text-sm">Loading...</td></tr>}
              {!loading && visible.length === 0 && <tr><td colSpan={9} className="px-4 py-16 text-center text-slate-400 text-sm">No additional scope yet.</td></tr>}
              {visible.map(co => (
                <tr key={co.id} className="border-b border-slate-100 hover:bg-slate-50/40 transition-colors" data-testid={`co-row-${co.short_id}`}>
                  {/* Title cell — primary identifier. The auto-generated short_id
                      sits underneath as a small monospace sub-label, click-through
                      to the activity timeline. */}
                  <td className="px-4 py-3 max-w-[260px]">
                    <button
                      onClick={() => setActivityCo(co)}
                      className="block text-left w-full group"
                      data-testid={`co-activity-${co.short_id}`}
                    >
                      <div className="font-semibold text-slate-900 text-[13px] leading-snug group-hover:text-slate-700 truncate">
                        {co.title || co.description || "Untitled"}
                      </div>
                      <div className="font-mono text-[10px] text-slate-400 mt-0.5 group-hover:text-slate-500">
                        {co.short_id}
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 text-[13px]">{co.project_name || "—"}</div>
                    <div className="text-[11px] text-slate-400 truncate max-w-xs">{co.description}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`text-[10px] font-medium ${co.request_type === "upsell" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                      {co.request_type === "upsell" ? "Upsell" : "Change Order"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[12px] text-slate-600 capitalize">{co.category.replace(/_/g, " ")}</div>
                    <div className="text-[10px] text-slate-400 capitalize">{co.scope_type}</div>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-600">{co.pm_name || "—"}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`text-[10px] font-medium capitalize ${STATUS_TONE[co.status] || ""}`}>{STATUS_LABEL[co.status]}</Badge>
                    {co.is_late_entry && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-600"><AlertTriangle className="w-2.5 h-2.5" /> Late Entry</div>
                    )}
                    {linkDaysRemaining(co) !== null && (
                      <div className={`mt-1 inline-flex items-center gap-1 text-[10px] ${linkDaysRemaining(co) <= 2 ? "text-red-600" : "text-slate-400"}`}>
                        <Clock className="w-2.5 h-2.5" />
                        {linkDaysRemaining(co) === 0 ? "Link expires today" : `Link expires in ${linkDaysRemaining(co)}d`}
                      </div>
                    )}
                  </td>
                  {showMoney && (
                    <td className="px-4 py-3 text-right tabular-nums">
                      <div className="font-semibold text-slate-900">{fmt$(co.total_amount)}</div>
                      {co.itemized && co.proposed_total > (co.total_amount || 0) + 0.005 && (
                        <div className="text-[10px] text-slate-400 font-normal">of {fmt$(co.proposed_total)} proposed</div>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-[11px] text-slate-500">
                    <div>{fmtDate(co.approved_at)}</div>
                    {decisionSummary(co) && <div className="text-[10px] text-slate-400 mt-0.5">{decisionSummary(co)}</div>}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {/* Edit — reopens the intake wizard on this row. Finance
                        edits anything; everyone else their own unsent rows. */}
                    {canEditAdditionalScope(co, { isFinance, user }) && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => setEditCo(co)} data-testid={`edit-${co.short_id}`}>
                        <Pencil className="w-3 h-3" /> Edit
                      </Button>
                    )}
                    {/* Primary: send to client (draft / ready_to_send) */}
                    {(co.status === "draft" || co.status === "ready_to_send") && (
                      <Button size="sm" className="h-7 text-[11px] gap-1" onClick={() => handleSendToClient(co)} data-testid={`send-client-${co.short_id}`}>
                        <Mail className="w-3 h-3" /> Send to Client
                      </Button>
                    )}
                    {/* Re-send option if already sent */}
                    {co.status === "sent_to_client" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => handleSendToClient(co)} data-testid={`resend-client-${co.short_id}`}>
                        <Mail className="w-3 h-3" /> Resend
                      </Button>
                    )}
                    {/* Copy approval link */}
                    {co.approval_url && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => navigator.clipboard.writeText(co.approval_url).then(() => toast.success("Approval link copied"))}>
                        Copy Link
                      </Button>
                    )}
                    {co.status === "cleared_for_production" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleStartProduction(co.id)} data-testid={`start-${co.short_id}`}>Start Production</Button>
                    )}
                    {co.status === "in_production" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleComplete(co.id)} data-testid={`complete-${co.short_id}`}>Complete</Button>
                    )}
                    {/* Upsell: send balance invoice after completion */}
                    {co.status === "completed" && co.request_type === "upsell" && !co.balance_paid && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => handleSendBalanceInvoice(co)} data-testid={`balance-invoice-${co.short_id}`}>
                        Send Final Invoice
                      </Button>
                    )}
                    {/* Manual payment — for money received outside the Stripe link (finance only) */}
                    {showMoney && ["ready_to_send", "sent_to_client", "client_approved", "invoice_sent", "payment_pending", "invoiced", "approved"].includes(co.status) && !co.deposit_paid && co.stripe_payment_status !== "paid" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1 border-green-300 text-green-700 hover:bg-green-50" onClick={() => setManualPayCo(co)} data-testid={`manual-payment-${co.short_id}`}>
                        <DollarSign className="w-3 h-3" /> Record Payment
                      </Button>
                    )}
                    {/* Legacy flows */}
                    {co.status === "pending_approval" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleApprove(co.id)}>Approve</Button>
                    )}
                    {co.status === "pending_sales_review" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleStatus(co.id, "ready_to_send")}>Approve Pricing</Button>
                    )}
                    {co.status === "invoiced" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleStatus(co.id, "payment_pending")}>Payment Pending</Button>
                    )}
                    {co.status === "payment_pending" && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleStatus(co.id, "paid")}>Mark Paid</Button>
                    )}
                    {/* Delete — for test rows and mistakes. Finance deletes
                        anything; everyone else their own not-yet-sent rows. */}
                    {canDeleteAdditionalScope(co, { isFinance, user }) && (
                      <Button size="sm" variant="ghost" className="h-7 text-[11px] gap-1 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleteCo(co)} data-testid={`delete-${co.short_id}`}>
                        <Trash2 className="w-3 h-3" /> Delete
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>

        {/* By project breakdown */}
        {showMoney && analytics && analytics.by_project?.length > 0 && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Card className="p-5">
              <h3 className="text-[12px] uppercase tracking-wider text-slate-400 mb-3">Top Projects</h3>
              <div className="space-y-2">
                {analytics.by_project.slice(0, 5).map(p => (
                  <div key={p.project_id} className="flex justify-between gap-3 text-[13px]">
                    <span className="text-slate-700 truncate min-w-0">{p.project_name}</span>
                    <span className="font-semibold tabular-nums flex-shrink-0">{fmt$(p.total)}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="text-[12px] uppercase tracking-wider text-slate-400 mb-3">By Project Manager</h3>
              <div className="space-y-2">
                {analytics.by_pm.slice(0, 5).map(p => (
                  <div key={p.pm_name} className="flex justify-between gap-3 text-[13px]">
                    <span className="text-slate-700 truncate min-w-0">{p.pm_name}</span>
                    <span className="font-semibold tabular-nums flex-shrink-0">{fmt$(p.total)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Billing rates editor — collapsible */}
        <BillingRatesPanel />

        <AddChangeOrderModal open={addOpen} onOpenChange={setAddOpen} projectId="" projectName="" onSaved={() => load()} forcedType="change_order" />

        {/* Edit — the same wizard, hydrated from an existing row. Keyed on the
            row id so switching between two orders remounts with fresh state. */}
        {editCo && (
          <AddChangeOrderModal
            key={editCo.id}
            open
            onOpenChange={(v) => { if (!v) setEditCo(null); }}
            editingCo={editCo}
            projectId={editCo.project_id}
            projectName={editCo.project_name}
            forcedType="change_order"
            onSaved={() => { setEditCo(null); load(); }}
          />
        )}

        {/* Invoice / Email draft preview */}
        <Dialog open={!!invoiceDraft} onOpenChange={() => setInvoiceDraft(null)}>
          <DialogContent className="max-w-xl" data-testid="invoice-draft-modal">
            <DialogHeader>
              <DialogTitle>Invoice Generated</DialogTitle>
              <DialogDescription>{invoiceDraft?.invoice?.invoice_id}</DialogDescription>
            </DialogHeader>
            {invoiceDraft && (
              <div className="space-y-4 py-2">
                <div className="bg-slate-50 border rounded-md p-4 text-[13px]">
                  <div className="flex justify-between"><span className="text-slate-500">Project</span><span className="font-medium">{invoiceDraft.invoice.project_name || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-semibold">{fmt$(invoiceDraft.invoice.total_amount)}</span></div>
                  <div className="mt-2 pt-2 border-t text-[12px] text-slate-500">Stripe integration: <span className="font-medium text-slate-700">pending_configuration</span></div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400 mb-2"><Mail className="w-3 h-3" /> Email Draft</div>
                  <div className="bg-slate-50 border rounded-md p-4 space-y-2 text-[13px]">
                    <div><span className="text-slate-500 text-[11px]">Subject:</span> <span className="font-medium">{invoiceDraft.email_draft.subject}</span></div>
                    <pre className="text-[12px] text-slate-700 whitespace-pre-wrap font-sans">{invoiceDraft.email_draft.body}</pre>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setInvoiceDraft(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 2 — Send-to-client email composer */}
        <SendClientEmailDialog
          co={emailingCo}
          onClose={() => setEmailingCo(null)}
          onSent={() => { setEmailingCo(null); load(); }}
        />

        {/* Manual payment recording */}
        <RecordManualPaymentDialog
          co={manualPayCo}
          paymentType="deposit"
          onClose={() => setManualPayCo(null)}
          onSaved={() => { setManualPayCo(null); load(); }}
        />

        {/* Delete confirmation */}
        <DeleteAdditionalScopeDialog
          co={deleteCo}
          showMoney={showMoney}
          onClose={() => setDeleteCo(null)}
          onDeleted={() => { setDeleteCo(null); load(); }}
        />

        {/* Phase 3 — Activity timeline drawer */}
        <ActivityDrawer co={activityCo} showMoney={showMoney} onClose={() => setActivityCo(null)} />
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon }) {
  return (
    <Card className="p-3 sm:p-5 border-slate-200 min-w-0">
      <div className="flex items-start justify-between mb-2 gap-1">
        <span className="text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-400 truncate">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />}
      </div>
      <div className="text-base sm:text-[22px] font-light tabular-nums text-slate-900 truncate">{value}</div>
    </Card>
  );
}

function ActivityDrawer({ co, showMoney, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!co) { setDetail(null); return; }
    apiClient.get(`/change-orders/${co.id}`)
      .then(res => setDetail(res.data))
      .catch(() => setDetail(co)); // fall back to row data
  }, [co]);
  if (!co) return null;

  const data = detail || co;
  const events = (data.history || []).slice().reverse();
  const emails = (data.email_events || []).slice().reverse();

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="co-activity-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-white shadow-2xl border-l border-slate-200 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Activity Timeline</div>
            <div className="text-base font-semibold text-slate-900 font-mono">{data.short_id}</div>
            <div className="text-[12px] text-slate-500 truncate max-w-[280px]">{data.project_name} · {data.title || data.category}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="co-activity-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-md border border-slate-200 p-3 bg-slate-50/40">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Status</div>
            <Badge variant="outline" className="text-[11px] capitalize">{(data.status || "").replace(/_/g, " ")}</Badge>
            {data.admin_override && (
              <Badge variant="outline" className="ml-2 text-[10px] bg-amber-50 text-amber-700 border-amber-200">Admin Override</Badge>
            )}
          </div>

          {data.itemized && (data.line_items || []).length > 0 && (
            <ScopeItemDecisions co={data} showMoney={showMoney} />
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><Activity className="w-3 h-3" /> Workflow History</div>
            <div className="space-y-2">
              {events.length === 0 && <div className="text-[12px] text-slate-400">No history yet.</div>}
              {events.map((e, idx) => (
                <div key={idx} className="border-l-2 border-slate-200 pl-3 py-0.5">
                  <div className="text-[12px] font-medium text-slate-900 capitalize">{(e.event_type || e.event || e.action || "").replace(/_/g, " ")}</div>
                  <div className="text-[11px] text-slate-500">
                    {e.actor_email || e.user_email || "system"} · {new Date(e.at || e.ts || e.timestamp || Date.now()).toLocaleString()}
                  </div>
                  {(e.detail || e.note) && <div className="text-[12px] text-slate-600 mt-0.5">{e.detail || e.note}</div>}
                </div>
              ))}
            </div>
          </div>

          {emails.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email Events</div>
              <div className="space-y-1.5">
                {emails.map((e, idx) => (
                  <div key={idx} className="text-[11px] border border-slate-200 rounded px-2 py-1.5 bg-white">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700 capitalize">{(e.event || "").replace(/_/g, " ")}</span>
                      <span className={e.ok ? "text-emerald-600" : "text-red-600"}>{e.ok ? "delivered" : "failed"}</span>
                    </div>
                    <div className="text-slate-400 truncate">{(e.to || []).join(", ")}</div>
                    {e.subject && <div className="text-slate-600 truncate italic">{e.subject}</div>}
                    {e.error && <div className="text-red-500 truncate">{e.error}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.stripe_payment_link && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Stripe</div>
              <div className="text-[11px] font-mono text-slate-500 truncate">{data.stripe_session_id}</div>
              <div className="text-[11px] mt-1">Status: <span className="capitalize font-medium text-slate-700">{data.stripe_payment_status}</span></div>
              <a href={data.stripe_payment_link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-600 hover:underline">
                Open payment link
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Which parts of an itemized change order the client authorized. PMs see the
// decisions without the prices (the backend scrubs them).
function ScopeItemDecisions({ co, showMoney }) {
  const lines = co.line_items || [];
  const summary = decisionSummary(co);
  return (
    <div data-testid="co-scope-decisions">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 flex items-center justify-between">
        <span>Scope Items</span>
        {summary && <span className="normal-case tracking-normal text-slate-500">{summary}</span>}
      </div>
      <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
        {lines.map(li => {
          const decision = itemDecision(li, co);
          return (
            <div key={li.id} className="flex items-start justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className={`text-[12.5px] font-medium ${decision === "declined" ? "text-slate-400 line-through" : "text-slate-900"}`}>{li.item_name}</div>
                {li.category && <div className="text-[10.5px] text-slate-400">{li.category}</div>}
              </div>
              <div className="text-right shrink-0">
                {showMoney && li.total_price != null && (
                  <div className={`text-[12.5px] tabular-nums ${decision === "declined" ? "text-slate-400 line-through" : "text-slate-900 font-medium"}`}>{fmt$(li.total_price)}</div>
                )}
                <Badge variant="outline" className={`text-[9.5px] mt-0.5 ${DECISION_TONE[decision]}`}>{DECISION_LABEL[decision]}</Badge>
              </div>
            </div>
          );
        })}
      </div>
      {showMoney && co.proposed_total != null && (
        <div className="mt-2 flex items-center justify-between text-[12px]">
          <span className="text-slate-500">Approved total</span>
          <span className="tabular-nums">
            <span className="font-semibold text-slate-900">{fmt$(co.total_amount)}</span>
            {co.proposed_total > (co.total_amount || 0) + 0.005 && <span className="text-slate-400"> of {fmt$(co.proposed_total)} proposed</span>}
          </span>
        </div>
      )}
    </div>
  );
}

function SendClientEmailDialog({ co, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [includePaymentLink, setIncludePaymentLink] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!co) return;
    setSubject(`Change Order for ${co.project_name || "your project"}`);
    setBody(
      `<p>Hi ${co.client_name || "there"},</p>` +
      `<p>Attached is the change order outlining the requested additional scope for <strong>${co.project_name || "your project"}</strong>.</p>` +
      `<p>You can review the scope summary and complete payment using the link below.</p>` +
      `<p>Once payment is completed, our production team will be cleared to begin.</p>` +
      `<p>Best,<br/>${co.sales_rep_name || "The Render Pros"}</p>`
    );
    setTo(""); setCc(""); setBcc("");
    setIncludePaymentLink(!!co.stripe_payment_link);
  }, [co]);

  const send = async () => {
    if (!to.trim()) { toast.error("At least one recipient is required"); return; }
    setSending(true);
    try {
      const res = await apiClient.post(`/change-orders/${co.id}/send-email`, {
        to: to.split(",").map(s => s.trim()).filter(Boolean),
        cc: cc.split(",").map(s => s.trim()).filter(Boolean),
        bcc: bcc.split(",").map(s => s.trim()).filter(Boolean),
        subject, body_html: body, include_payment_link: includePaymentLink,
      });
      const emailId = res.data?.email_id;
      // Surface the Resend message ID in the toast — gives Sales an instant
      // sanity-check that the email actually left HQ (verifiable in Resend dashboard).
      toast.success(emailId ? `Email sent (ID: ${emailId.slice(0, 8)}…)` : "Email sent to client");
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={!!co} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="co-email-composer">
        <DialogHeader>
          <DialogTitle>Send to Client</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input placeholder="To (comma separated)" value={to} onChange={(e) => setTo(e.target.value)} className="sm:col-span-3" data-testid="co-email-to" />
            <Input placeholder="CC" value={cc} onChange={(e) => setCc(e.target.value)} />
            <Input placeholder="BCC" value={bcc} onChange={(e) => setBcc(e.target.value)} className="sm:col-span-2" />
          </div>
          <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="co-email-subject" />
          <textarea
            className="w-full min-h-[180px] p-3 border border-slate-200 rounded-md text-[13px] font-mono"
            value={body} onChange={(e) => setBody(e.target.value)}
            data-testid="co-email-body"
          />
          <label className="flex items-center gap-2 text-[12px] text-slate-600">
            <input type="checkbox" checked={includePaymentLink} onChange={(e) => setIncludePaymentLink(e.target.checked)} />
            Include Stripe payment link as CTA button
          </label>
          {co?.stripe_payment_link && (
            <div className="text-[11px] text-slate-400 truncate">Payment link: {co.stripe_payment_link.slice(0, 80)}…</div>
          )}
          {!co?.stripe_payment_link && includePaymentLink && (
            <div className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              No Stripe payment link generated yet — the email will send without one.
            </div>
          )}
          {co?.sales_rep_email && (
            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
              The salesperson ({co.sales_rep_email}) will be auto-CC'd on this email so you receive a copy in your inbox.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="co-email-send">{sending ? "Sending…" : "Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Billing Rates Panel ───────────────────────────────────────────────────
const ARTIST_LABELS = {
  "3d_artist":            "3D Artist",
  "senior_3d_artist":     "Senior 3D Artist",
  "animator":             "Animator",
  "vexos_developer":      "VEXOS Developer",
  "designer":             "Designer",
  "marketing_specialist": "Marketing Specialist",
};

// Simplified July 2026: just Regular / Rush ("priority" is legacy-only and hidden).
const URGENCY_LABELS = {
  standard: "Regular",
  rush:     "Rush (+50%)",
};

function BillingRatesPanel() {
  const [open, setOpen] = useState(false);
  const [rates, setRates] = useState(null);
  const [urgency, setUrgency] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open || rates) return;
    apiClient.get("/change-orders/settings/billable-rates")
      .then(r => {
        setRates(r.data.rates);
        setUrgency(r.data.urgency_multipliers);
        setDraft({ ...r.data.rates });
      })
      .catch(() => toast.error("Could not load billing rates"));
  }, [open, rates]);

  const handleChange = (key, val) => {
    const num = parseFloat(val);
    setDraft(d => ({ ...d, [key]: isNaN(num) ? val : num }));
    setDirty(true);
  };

  const save = async () => {
    const invalid = Object.entries(draft).filter(([, v]) => isNaN(parseFloat(v)) || parseFloat(v) <= 0);
    if (invalid.length) { toast.error("All rates must be positive numbers"); return; }
    setSaving(true);
    try {
      const r = await apiClient.patch("/change-orders/settings/billable-rates", { rates: draft });
      setRates(r.data.rates);
      setDraft({ ...r.data.rates });
      setDirty(false);
      toast.success("Billing rates saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save — Finance access required");
    } finally { setSaving(false); }
  };

  const reset = () => { setDraft({ ...rates }); setDirty(false); };

  return (
    <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden bg-white">
      {/* Header row — always visible */}
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-2.5">
          <Settings className="w-4 h-4 text-slate-400" />
          <span className="text-[13px] font-semibold text-slate-800">Billing Rates</span>
          <span className="text-[11px] text-slate-400">· edit internal hourly rates per artist type</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 px-5 pb-5 pt-4">
          {!rates ? (
            <p className="text-[13px] text-slate-400">Loading…</p>
          ) : (
            <>
              {/* Rates grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                {Object.entries(ARTIST_LABELS).map(([key, label]) => (
                  <div key={key} className="space-y-1">
                    <label className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-slate-400 pointer-events-none">$</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={draft[key] ?? ""}
                        onChange={e => handleChange(key, e.target.value)}
                        className="w-full h-9 pl-6 pr-10 border border-slate-200 rounded-md text-[13px] font-mono bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 pointer-events-none">/hr</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Urgency multipliers — read-only reference */}
              {urgency && (
                <div className="mb-5 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-[10.5px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Urgency Multipliers (fixed)</div>
                  <div className="flex gap-5">
                    {Object.entries(urgency).filter(([key]) => URGENCY_LABELS[key]).map(([key, val]) => (
                      <div key={key} className="text-[12px] text-slate-600">
                        <span className="font-medium text-slate-800">{URGENCY_LABELS[key]}:</span>{" "}
                        <span className="font-mono">{val}×</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={save} disabled={saving || !dirty} className="h-8 text-[12px]">
                  {saving ? "Saving…" : "Save Changes"}
                </Button>
                {dirty && (
                  <Button size="sm" variant="ghost" onClick={reset} className="h-8 text-[12px] text-slate-500">
                    Discard
                  </Button>
                )}
                {!dirty && (
                  <span className="text-[11px] text-slate-400">All changes saved</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
